import { AutofillFlatRateButton } from "components/AutofillButtons";
import { CSVFrozenHeaderMapping } from "csv/mappings";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useState } from "react";
import { RowsChangeData } from "react-data-grid";
import { useAsync } from "react-use";
import api from "../../api";
import { APICountryListSearchParams } from "../../api/countries";
import { APIInputSheetFileType } from "../../api/inputSheets";
import GenerateOutputsButton from "../../components/GenerateOutputsButton";
import Loading from "../../components/Loading";
import {
    getFrozenMappingOHLogs,
    getFrozenMappingOHMfg,
    getFrozenMappingRMWorking,
    mappingCapex,
    mappingMarginalCostPctCountry,
    mappingMarginalCostPctPlant,
    mappingOHLogs,
    mappingOHPackMfg,
    mappingRMWorking,
} from "../../csv/mappings/collect";
import { IRow } from "../../csv/types";
import { parseRows } from "../../csv/utils";
import { useStore } from "../../store";
import InputSheet from "../../store/models/InputSheet";
import { Toast } from "../../utils";
import WorkArea from "../WorkArea";
import { useProject } from "../Workspace/hooks";
import { CsvFile, TabList } from "../Workspace/types";
import CLTAssumptionsTab from "./CLTAssumptionsTab";
import { useProjectState } from "./hooks";
import {
    schemaCapex,
    schemaMarginalCostPctCountry,
    schemaMarginalCostPctPlant,
    schemaOHLogs,
    schemaOHPackMfg,
    schemaRMWorking,
} from "./schema";
import {
    marginalCostPctValidations,
    ohLogsValidations,
    ohPackMfgValidations,
    rmWorkingValidations,
} from "./validations";
import {
    ohLogsValueParsers,
    ohPackMfgValueParsers,
    rmWorkingValueParsers,
} from "./valueParsers";

type Props = {};

// RM Working columns whose values are sourced from the DEFINE RM Price Assumptions (RMPA)
// sheet when a row's "Price Obtained From" is set to RMPA, and should be reset to 0 for
// any other price source.
const RMPA_DERIVED_COLUMNS = [
  "rm_purchase_price",
  "supplier_freight",
  "drumming_differential_per_mt",
  "rebates_per_mt",
  "discounts_per_mt",
  "supply_storage_handling_fee",
  "tax_duties",
  "other_costs",
] as const;

const normalizeRmpaKeyPart = (value: unknown) =>
  (value ?? "").toString().trim().toLowerCase();

const buildRmpaKey = (
  rCode: unknown,
  productName: unknown,
  plant: unknown,
  bulkOrDrum: unknown
) =>
  [rCode, productName, plant, bulkOrDrum].map(normalizeRmpaKeyPart).join("|");

const buildRmpaExSupplierKey = (
  rCode: unknown,
  productName: unknown,
  bulkOrDrum: unknown
) => [rCode, productName, bulkOrDrum].map(normalizeRmpaKeyPart).join("|");

const CollectSection: React.FC<Props> = () => {
  const store = useStore();
  const project = useProject();
  const currentUser = store.auth.current;

  const hasEditPermission = !!(
    currentUser &&
    (currentUser.isAdmin ||
      currentUser.isAnalyst ||
      project?.adminUsers.includes(currentUser.id))
  );
  const editable = !project?.isLocked && hasEditPermission;

  useEffect(() => {
    (async () => {
      if (project) {
        const countryParams: APICountryListSearchParams = {
          project_id: project.id.toString(),
        };
        await store.assets.listCountries(undefined, undefined, countryParams);
      }
    })();
  }, [project, store.assets]);

  useProjectState(project, currentUser);

  const sheets = project
    ? store.inputSheets.ofProject(project.id)
    : new Map<APIInputSheetFileType, InputSheet[]>();

  // Lookups (keyed by r_code|supplier_product_name|plant|bulk_or_drum, and a fallback
  // for "Ex Supplier" RMPA rows keyed without plant) used to auto-populate RM Working
  // rows when a row's Price Obtained From is switched to RMPA.
  const [rmpaLookup, setRmpaLookup] = useState<Map<string, Record<string, any>>>(
    new Map()
  );
  const [rmpaExSupplierLookup, setRmpaExSupplierLookup] = useState<
    Map<string, Record<string, any>>
  >(new Map());

  useAsync(async () => {
    if (!project) {
      return;
    }

    await store.defineSheets.list(project.id, undefined, undefined, {
      file_type: "rm_price_assumptions",
    });

    const rmpaSheets =
      store.defineSheets.ofProject(project.id).get("rm_price_assumptions") ||
      [];

    const lookup = new Map<string, Record<string, any>>();
    const exSupplierLookup = new Map<string, Record<string, any>>();

    await Promise.all(
      rmpaSheets.map(async (sheet) => {
        if (!sheet.file) {
          return;
        }
        const response = await api.csv.get(sheet.file);
        const csvString = await response.text();
        const rows = parseRows<Record<string, any>>(csvString);

        rows.forEach((row) => {
          const rCode = row["rcode"];
          const productName = row["raw_material_desc"];
          const plant = row["plant"];
          const bulkOrDrum = row["pack_type"];

          if (!normalizeRmpaKeyPart(rCode) && !normalizeRmpaKeyPart(productName)) {
            return;
          }

          lookup.set(buildRmpaKey(rCode, productName, plant, bulkOrDrum), row);

          if (normalizeRmpaKeyPart(plant) === "ex supplier") {
            exSupplierLookup.set(
              buildRmpaExSupplierKey(rCode, productName, bulkOrDrum),
              row
            );
          }
        });
      })
    );

    setRmpaLookup(lookup);
    setRmpaExSupplierLookup(exSupplierLookup);
  }, [project, store.defineSheets]);

  const handleRMWorkingRowsChange = useCallback(
    (rows: IRow[], data?: RowsChangeData<IRow, unknown>) => {
      if (!data || data.column.key !== "price_obtained_from") {
        return rows;
      }

      // Lookups may not have loaded yet; avoid wiping out valid data in that case.
      if (rmpaLookup.size === 0 && rmpaExSupplierLookup.size === 0) {
        return rows;
      }

      const nextRows = [...rows];

      data.indexes.forEach((index) => {
        const row = { ...nextRows[index] };

        if (row["price_obtained_from"] !== "RMPA") {
          RMPA_DERIVED_COLUMNS.forEach((col) => {
            row[col] = 0;
          });
          row["total"] = 0;
        } else {
          const match =
            rmpaLookup.get(
              buildRmpaKey(
                row["r_code"],
                row["supplier_product_name"],
                row["plant"],
                row["bulk_or_drum"]
              )
            ) ||
            rmpaExSupplierLookup.get(
              buildRmpaExSupplierKey(
                row["r_code"],
                row["supplier_product_name"],
                row["bulk_or_drum"]
              )
            );

          if (match) {
            row["rm_purchase_price"] = match["rm_purchase_price"] ?? 0;
            row["supplier_freight"] = match["supplier_freight"] ?? 0;
            row["drumming_differential_per_mt"] = match["drumming_diff/mt"] ?? 0;
            row["rebates_per_mt"] = match["rebates_per_mt"] ?? 0;
            row["discounts_per_mt"] = match["discounts_per_mt"] ?? 0;
            row["supply_storage_handling_fee"] =
              match["supply_storage_handling_fee"] ?? 0;
            row["tax_duties"] = match["tax_duties"] ?? 0;
            row["other_costs"] = match["other_costs"] ?? 0;
          } else {
            RMPA_DERIVED_COLUMNS.forEach((col) => {
              row[col] = 0;
            });
            Toast.danger(
              `No matching RM Price Assumption found for ${
                row["supplier_product_name"] || row["r_code"] || "this row"
              }.`
            );
          }
        }

        nextRows[index] = row;
      });

      return nextRows;
    },
    [rmpaLookup, rmpaExSupplierLookup]
  );

  const [frozenRMWorking, setFrozenRMWorking] =
    useState<CSVFrozenHeaderMapping>(getFrozenMappingRMWorking(true));
  const [frozenOHLogs, setFrozenOHLogs] = useState<CSVFrozenHeaderMapping>(
    getFrozenMappingOHLogs(true)
  );
  const [frozenOHMfg, setFrozenOHMfg] = useState<CSVFrozenHeaderMapping>(
    getFrozenMappingOHMfg(true)
  );

  const tabs: TabList = {
    rmWorking: {
      kind: "worksheet",
      id: "rmWorking",
      title: "RM Working",
      type: "sheet-collect",
      fileType: "rm_working",
      validationChecks: rmWorkingValidations,
      valueParsers: rmWorkingValueParsers,
      frozenColumns: frozenRMWorking,
      setFrozenColumns: setFrozenRMWorking,
      csvFiles:
        sheets.get("rm_working")?.map((sheet) => {
          const cltLocked = project?.lockedClusters.includes(sheet.cluster);
          const sheetEditable = !cltLocked;

          return [
            {
              id: `rmWorking-${sheet.id}`,
              sheet,
              downloadable: true,
              editable: editable && sheetEditable,
              updatable: editable && sheetEditable,
              title: sheet.clusterName,
              schema: (key, sheetEditable) =>
                schemaRMWorking(key, sheetEditable, frozenRMWorking),
              headerMapping: mappingRMWorking,
              rowIdentifier: "concatenate",
              expectedColumns: Object.keys(mappingRMWorking),
              onRowsChange: handleRMWorkingRowsChange,
              extraHeaderRow: [
                {
                  id: "",
                  rm_purchase_price:
                    "Supplier Price List Information (RM Supplier Invoiced Costs Only) - Values in price/unit",
                  cost_per_mt: "Landed Cost per MT",
                  freight_cost: "price/unit",
                } as any,
              ],
              hideColumns: () => ["raw_material_id", "concatenate", "freight_cost"],
            },
          ];
        }) || [],
      marginalCsvFiles: [],

      async csvLoader() {
        if (!project) {
          return;
        }
        await store.inputSheets.list(project.id, undefined, undefined, {
          file_type: "rm_working",
        });
      },
    },
    marginalCostPct: {
      kind: "worksheet",
      id: "marginalCostPct",
      title: "Marginal Cost %",
      type: "sheet-collect",
      fileType: "marginal_cost_percent_country",
      uploadable: false,
      validationChecks: marginalCostPctValidations,
      csvFiles: (() => {
        const marginalCostPctSheets = (
          sheets.get("marginal_cost_percent_country")?.map((sheet) => {
            const cltLocked = project?.lockedClusters.includes(sheet.cluster);
            const sheetEditable = !cltLocked;

            return {
              id: `marginalCostPctCountry-${sheet.id}`,
              sheet,
              downloadable: true,
              editable: editable && sheetEditable,
              updatable: editable && sheetEditable,
              title: sheet.clusterName,
              schema: schemaMarginalCostPctCountry,
              headerMapping: mappingMarginalCostPctCountry,
              rowIdentifier: "id",
            };
          }) || []
        )
          .concat(
            sheets.get("marginal_cost_percent_plants")?.map((sheet) => {
              const cltLocked = project?.lockedClusters.includes(sheet.cluster);
              const sheetEditable = !cltLocked;

              return {
                id: `marginalCostPctPlant-${sheet.id}`,
                sheet,
                downloadable: true,
                editable: editable && sheetEditable,
                updatable: editable && sheetEditable,
                title: sheet.clusterName,
                schema: schemaMarginalCostPctPlant,
                headerMapping: mappingMarginalCostPctPlant,
                rowIdentifier: "id",
              };
            }) || []
          )
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr.title]: [...(acc[curr.title] || []), curr],
            }),
            {} as Record<string, CsvFile[]>
          );
        return Object.values(marginalCostPctSheets);
      })(),
      marginalCsvFiles: [],

      async csvLoader() {
        if (!project) {
          return;
        }
        await store.inputSheets.list(project.id, undefined, undefined, {
          file_type__in: [
            "marginal_cost_percent_country",
            "marginal_cost_percent_plants",
          ],
        });
      },
    },
    ohPackMfg: {
      kind: "worksheet",
      id: "ohPackMfg",
      title: "OH Pack Mfg",
      type: "sheet-collect",
      fileType: "oh_full_pack_mfg",
      validationChecks: ohPackMfgValidations,
      valueParsers: ohPackMfgValueParsers,
      frozenColumns: frozenOHMfg,
      setFrozenColumns: setFrozenOHMfg,
      comments:
        "Note: All inputs must be entered as cost per liter unless indicated as %",
      csvFiles:
        sheets.get("oh_full_pack_mfg")?.map((sheet) => {
          const cltLocked = project?.lockedClusters.includes(sheet.cluster);
          const sheetEditable = !cltLocked;

          return [
            {
              id: `ohPackMfg-${sheet.id}`,
              sheet,
              downloadable: true,
              editable: editable && sheetEditable,
              updatable: editable && sheetEditable,
              title: sheet.clusterName,
              rowIdentifier: "concatenate",
              schema: (key, sheetEditable) =>
                schemaOHPackMfg(
                  key,
                  sheetEditable,
                  sheet.clusterName,
                  frozenOHMfg
                ),
              headerMapping: mappingOHPackMfg,
              hideColumns: () => [
                "source_clt",
                "repackaging_clt",
                "concatenate",
              ],
            },
          ];
        }) || [],
      marginalCsvFiles:
        sheets.get("oh_marg_pack_mfg")?.map((sheet) => [
          {
            id: `ohMarginalPackMfg-${sheet.id}`,
            sheet,
            downloadable: true,
            editable: false,
            updatable: false,
            title: sheet.clusterName,
            schema: (key, sheetEditable) =>
              schemaOHPackMfg(
                key,
                sheetEditable,
                sheet.clusterName,
                frozenOHMfg
              ),
            rowIdentifier: "concatenate",
            headerMapping: mappingOHPackMfg,
            hideColumns: () => ["source_clt", "repackaging_clt", "concatenate"],
          },
        ]) || [],
      renderExtraToolbar: (worksheet) => (
        <>
          <GenerateOutputsButton
            text="Update marginal sheets"
            onClick={worksheet.csvLoader}
          />
          <AutofillFlatRateButton files={worksheet.csvFiles} />
        </>
      ),

      async csvLoader() {
        if (!project) {
          return;
        }
        await store.inputSheets.list(project.id, undefined, undefined, {
          file_type__in: ["oh_full_pack_mfg", "oh_marg_pack_mfg"],
        });
      },
    },
    ohLogs: {
      kind: "worksheet",
      id: "ohLogs",
      title: "OH Logs",
      type: "sheet-collect",
      fileType: "oh_full_logs",
      validationChecks: ohLogsValidations,
      valueParsers: ohLogsValueParsers,
      frozenColumns: frozenOHLogs,
      setFrozenColumns: setFrozenOHLogs,
      comments:
        "Note: All inputs must be entered as cost per liter unless indicated as %",
      csvFiles:
        sheets.get("oh_full_logs")?.map((sheet) => {
          const cltLocked = project?.lockedClusters.includes(sheet.cluster);
          const sheetEditable = !cltLocked;

          return [
            {
              id: `ohLogs-${sheet.id}`,
              sheet,
              downloadable: true,
              editable: editable && sheetEditable,
              updatable: editable && sheetEditable,
              title: sheet.clusterName,
              schema: (key, sheetEditable) => {
                return schemaOHLogs(
                  key,
                  sheetEditable,
                  sheet.clusterName,
                  frozenOHLogs
                );
              },
              headerMapping: mappingOHLogs,
              rowIdentifier: "concatenate",
              hideColumns: () => [
                "source_clt",
                "repackaging_clt",
                "concatenate",
              ],
            },
          ];
        }) || [],
      marginalCsvFiles:
        sheets.get("oh_marg_logs")?.map((sheet) => [
          {
            id: `ohMarginalLogs-${sheet.id}`,
            sheet,
            downloadable: true,
            editable: false,
            updatable: true,
            title: sheet.clusterName,
            schema: (key, sheetEditable) => {
              return schemaOHLogs(
                key,
                sheetEditable,
                sheet.clusterName,
                frozenOHLogs
              );
            },
            headerMapping: mappingOHLogs,
            rowIdentifier: "concatenate",
            hideColumns: () => ["source_clt", "repackaging_clt", "concatenate"],
          },
        ]) || [],
      renderExtraToolbar: (worksheet) => (
        <>
          <GenerateOutputsButton
            text="Update marginal sheets"
            onClick={worksheet.csvLoader}
          />
          <AutofillFlatRateButton files={worksheet.csvFiles} />
        </>
      ),

      async csvLoader() {
        if (!project) {
          return;
        }
        await store.inputSheets.list(project.id, undefined, undefined, {
          file_type__in: ["oh_full_logs", "oh_marg_logs"],
        });
      },
    },
    cltAssumptions: {
      kind: "custom",
      id: "cltAssumptions",
      title: "CLT Assumptions",
      type: "sheet-collect",
      render: CLTAssumptionsTab,
    },
    capex: {
      kind: "worksheet",
      id: "capex",
      title: "Capex Summary",
      type: "sheet-collect",
      fileType: "capex_summary",
      csvFiles:
        sheets.get("capex_summary")?.map((sheet) => {
          const cltLocked = project?.lockedClusters.includes(sheet.cluster);
          const sheetEditable = !cltLocked;

          return [
            {
              id: `capex-${sheet.id}`,
              sheet,
              downloadable: true,
              editable: editable && sheetEditable,
              insertable: editable && sheetEditable,
              updatable: editable && sheetEditable,
              title: sheet.clusterName,
              schema: schemaCapex,
              rowIdentifier: "id",
              headerMapping: mappingCapex,
            },
          ];
        }) || [],
      marginalCsvFiles: [],

      async csvLoader() {
        if (!project) {
          return;
        }
        await store.inputSheets.list(project.id, undefined, undefined, {
          file_type: "capex_summary",
        });
      },
    },
  };

  if (!project) {
    return <Loading full />;
  }

  return <WorkArea tabs={tabs} />;
};

export default observer(CollectSection);
